import * as yup from 'yup'
import { FacilityPurpose, IFacilityAddress } from 'apps/fleet-planner/utils/api/facilities'

const requiredMessage = 'This field is required.'

type AddressFields = 'addressee' | 'addressLine1' | 'addressLine2' | 'postalCode'

export interface INewAddress extends Pick<IFacilityAddress, AddressFields> {
  location: Pick<IFacilityAddress, 'locality' | 'subdivisionCode' | 'id'>
}

export interface IExistingFacilityAddress extends Pick<IFacilityAddress, AddressFields | 'id'> {
  location: Pick<IFacilityAddress, 'locality' | 'subdivisionCode' | 'id'>
}

/**
 * Used for creating a new address in POST /facilities
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Facilities/post_facilities
 */
export const AddressSchema = yup.object<INewAddress>({
  addressee: yup.string().notRequired(),
  addressLine1: yup.string().required(requiredMessage),
  addressLine2: yup.string().notRequired(),
  postalCode: yup.string().notRequired(),
  location: yup.object({
    /**
     * ID gives this value a unique identifier for
     * use with <LocationAutosuggest />
     */
    id: yup.string().notRequired(),
    locality: yup.string().required(requiredMessage),
    subdivisionCode: yup.string().required(requiredMessage),
  }),
})

export const ExistingFacilityAddressSchema = yup.object<IExistingFacilityAddress>({
  id: yup.string().required(),
  addressee: yup.string().notRequired(),
  addressLine1: yup.string().required(requiredMessage),
  addressLine2: yup.string().notRequired(),
  postalCode: yup.string().notRequired(),
  location: yup.object({
    /**
     * ID gives this value a unique identifier for
     * use with <LocationAutosuggest />
     */
    id: yup.string().required(),
    locality: yup.string().required(requiredMessage),
    subdivisionCode: yup.string().required(requiredMessage),
  }),
})

export type AddressSchemaType = yup.InferType<typeof AddressSchema>
export type ExistingFacilityAddressSchemaType = yup.InferType<typeof ExistingFacilityAddressSchema>

export type INewAddresRequestFields = AddressFields | 'locality' | 'subdivisionCode' | 'countryCode'
export type INewAddressRequest = Pick<IFacilityAddress, INewAddresRequestFields>

/**
 * Creates address request for POST /facilities
 * @param {AddressSchemaType} data - Form output
 * @param {Partial<INewAddressRequest>} overrides - Individually set values, outside of form schema context
 */
export const createNewAddressRequest = (
  data: AddressSchemaType,
  overrides: Partial<INewAddressRequest> = {}
): INewAddressRequest => {
  const { addressLine1 = '', addressLine2, addressee = '', location, postalCode } = data || {}
  const { locality, subdivisionCode } = location || {}
  return {
    addressee,
    addressLine1,
    addressLine2,
    postalCode,
    locality,
    subdivisionCode,
    countryCode: 'US',
    ...overrides,
  }
}

export const createUpdateAddressRequest = (
  data: ExistingFacilityAddressSchemaType,
  overrides: Partial<IFacilityAddress> = {}
): IFacilityAddress => {
  const { addressLine1 = '', addressLine2, addressee = '', id, location, postalCode } = data || {}
  const { locality, subdivisionCode } = location || {}
  return {
    id,
    addressee,
    addressLine1,
    addressLine2,
    postalCode,
    locality,
    subdivisionCode,
    countryCode: 'US',
    purpose: FacilityPurpose.facilityPrimary,
    ...overrides,
  }
}
